
import { useState } from "react";
import { format } from "date-fns";
import { Receipt, Pencil, Trash2, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Expense, ExpenseUser } from "@/types/expense";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface ExpenseItemProps {
  expense: Expense;
  currencies: {
    code: string;
    symbol: string;
  }[];
  onEditExpense: (expense: Expense) => void;
  onDeleteExpense?: (expenseId: string) => void;
  highlighted?: boolean;
}

export const ExpenseItem = ({
  expense,
  currencies,
  onEditExpense,
  onDeleteExpense,
  highlighted = false
}: ExpenseItemProps) => {
  const { t, i18n } = useTranslation();
  const currencySymbol = currencies.find(c => c.code === expense.currency)?.symbol || '';
  const showJpyAmount = expense.currency !== 'JPY' && expense.jpy_amount;
  const [showAllUsers, setShowAllUsers] = useState(false);
  
  // Format the date according to locale
  const formatExpenseDate = (dateString: string) => {
    const date = new Date(dateString);
    
    // For Japanese locale, use YYYY-MM-DD format
    if (i18n.language === 'ja') {
      return date.getFullYear() + '-' + 
        String(date.getMonth() + 1).padStart(2, '0') + '-' + 
        String(date.getDate()).padStart(2, '0');
    }
    
    // For other locales, use the default format from date-fns
    return format(date, 'MMM d, yyyy');
  };

  // Calculate how many avatars to show before "more" indicator
  const maxAvatarsToShow = 8;
  const hasMoreUsers = expense.sharedWithUsers.length > maxAvatarsToShow;
  const visibleUsers = showAllUsers 
    ? expense.sharedWithUsers
    : expense.sharedWithUsers.slice(0, maxAvatarsToShow);
  const hiddenUsersCount = expense.sharedWithUsers.length - maxAvatarsToShow;

  return (
    <div 
      className={cn(
        "p-3 rounded-lg bg-white/60 dark:bg-black/20 backdrop-blur-sm animate-fade-in relative border border-gray-200/50 dark:border-gray-800/50",
        highlighted && "border-primary/30"
      )}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-full bg-primary/20">
            <Receipt className="h-4 w-4 sm:h-4 sm:w-4 text-primary" />
          </div>
          <div>
            <div className="flex items-center">
              <h3 className="font-medium text-sm sm:text-base text-gray-900 dark:text-gray-100">{expense.title}</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 w-6 p-0 ml-1 hover:bg-transparent" 
                onClick={() => onEditExpense(expense)}
              >
                <Pencil className="h-3 w-3 text-gray-400 hover:text-primary" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Avatar className="h-4 w-4">
                <AvatarImage src={expense.paidByUser.avatar_url || undefined} />
                <AvatarFallback className="bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                  {expense.paidByUser.name.charAt(0).toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                {expense.paidByUser.name} <span className="text-gray-500 dark:text-gray-500">@{expense.paidByUser.username}</span>
              </p>
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="font-semibold text-sm sm:text-base text-slate-950 dark:text-slate-100">
            {currencySymbol}{expense.amount.toLocaleString()}
          </p>
          {showJpyAmount && (
            <p className="text-xs text-gray-500">
              (¥{expense.jpy_amount?.toLocaleString()})
            </p>
          )}
          <p className="text-[10px] sm:text-xs text-gray-500 whitespace-nowrap">
            {formatExpenseDate(typeof expense.date === 'string' ? expense.date : expense.date.toString())}
          </p>
        </div>
      </div>
      
      {/* Fancy Avatar Group for shared users - with just the Users icon and no text */}
      <div className="mt-3">
        <TooltipProvider>
          <div className="flex items-center">
            <div className="flex items-center gap-1">
              <Users className="h-3 w-3 text-gray-500" />
              <div className="flex -space-x-1 overflow-hidden ml-1">
                {visibleUsers.map((user, index) => (
                  <Tooltip key={user.id}>
                    <TooltipTrigger asChild>
                      <Avatar 
                        className={`
                          h-6 w-6 ring-2 ring-white dark:ring-gray-800
                          ${index % 5 === 0 ? 'animate-pulse-slow' : ''}
                          ${index % 5 === 1 ? 'hover:scale-110' : ''}
                          ${index % 5 === 2 ? 'hover:rotate-12' : ''}
                          ${index % 5 === 3 ? 'hover:-rotate-12' : ''}
                          ${index % 5 === 4 ? 'hover:translate-y-[-2px]' : ''}
                          transition-all duration-300 ease-in-out
                        `}
                      >
                        <AvatarImage src={user.avatar_url || undefined} />
                        <AvatarFallback className="bg-gradient-to-br from-primary/70 to-green/70 text-white text-xs">
                          {user.name.charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="bg-black/80 text-white text-xs py-1 px-2">
                      {user.name} @{user.username}
                    </TooltipContent>
                  </Tooltip>
                ))}
                
                {hasMoreUsers && !showAllUsers && (
                  <Button 
                    onClick={() => setShowAllUsers(true)}
                    className="h-6 w-6 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs p-0 ring-2 ring-white dark:ring-gray-800 hover:bg-primary hover:text-white transition-all"
                  >
                    +{hiddenUsersCount}
                  </Button>
                )}
                
                {showAllUsers && (
                  <Button 
                    onClick={() => setShowAllUsers(false)}
                    className="h-6 w-6 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs p-0 ring-2 ring-white dark:ring-gray-800 hover:bg-primary hover:text-white transition-all ml-1"
                  >
                    -
                  </Button>
                )}
              </div>
            </div>
          </div>
        </TooltipProvider>
      </div>
      
      {/* Delete button in bottom right - improved for mobile */}
      {onDeleteExpense && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="absolute bottom-2 right-2 h-12 w-12 p-0 hover:bg-red-100 dark:hover:bg-red-900/30 text-gray-400 hover:text-red-500 rounded-full touch-manipulation" 
          onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            onDeleteExpense(expense.id);
          }} 
          title={t('expenses.deleteExpense')} 
          aria-label={t('expenses.deleteExpense')}
        >
          <Trash2 className="h-5 w-5" />
          <span className="sr-only">{t('expenses.deleteExpense')}</span>
        </Button>
      )}
    </div>
  );
};
